import { evaluate, Icon } from '@eagle/react-common';
import type { ActivityTimelineItem } from '@ethings/ui-toolkit';
import { ActivityTimeline, Box, Stack } from '@ethings/ui-toolkit';
import { FC, useCallback, useEffect, useMemo, useRef, type JSX } from 'react';
import { ThingRangeTimelineProps } from '../trip-history-base-view.types';
import { VisibleTimelineMarker } from './visible-timeline-marker';
import { areItemIdsEqual, getOrderedVisibleItemIds } from './visible-timeline-marker-utils';

export const ThingRangeTimeline: FC<ThingRangeTimelineProps> = ({ hoveredEventId, itemTemplate, itemTemplateLoaded, items, markerTemplate, markerTemplateLoaded, onHoverEvent, onSelectBoundary, onSelectEvent, onVisibleItemIdsChange, selectedEventId }) => {
  const itemIdsRef = useRef<string[]>([]);
  const lastReportedVisibleItemIdsRef = useRef<string[]>([]);
  const visibleItemIdsRef = useRef<Set<string>>(new Set());
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);
  const timelineItems: ActivityTimelineItem[] = items.map((item) => {
    const itemContext = { event: item.event, item: { color: item.color, label: item.label, subtitle: typeof item.subtitle === 'string' ? item.subtitle : undefined, time: item.time, type: item.type } };
    return {
      color: item.color,
      id: item.id,
      interactive: true,
      kind: item.type as ActivityTimelineItem['kind'],
      meta: itemTemplateLoaded ? undefined : item.time,
      subtitle: itemTemplateLoaded ? undefined : item.subtitle,
      title: itemTemplateLoaded && itemTemplate ? evaluate(itemTemplate, itemContext) as string : item.label,
    };
  });

  const reportVisibleItemIds = useCallback(() => {
    const nextVisibleItemIds = getOrderedVisibleItemIds(itemIdsRef.current, visibleItemIdsRef.current);
    if (areItemIdsEqual(lastReportedVisibleItemIdsRef.current, nextVisibleItemIds)) return;

    lastReportedVisibleItemIdsRef.current = nextVisibleItemIds;
    onVisibleItemIdsChange?.(nextVisibleItemIds);
  }, [onVisibleItemIdsChange]);

  const handleVisibleChange = useCallback((itemId: string, visible: boolean) => {
    const visibleItemIds = visibleItemIdsRef.current;
    if (visibleItemIds.has(itemId) === visible) return;

    if (visible) {
      visibleItemIds.add(itemId);
    } else {
      visibleItemIds.delete(itemId);
    }

    reportVisibleItemIds();
  }, [reportVisibleItemIds]);

  useEffect(() => {
    itemIdsRef.current = itemIds;
    visibleItemIdsRef.current = new Set(Array.from(visibleItemIdsRef.current).filter((id) => itemIds.includes(id)));
    reportVisibleItemIds();
  }, [itemIds, reportVisibleItemIds]);

  return (
    <ActivityTimeline
      items={timelineItems}
      hoveredId={hoveredEventId ?? undefined}
      selectedId={selectedEventId ?? undefined}
      onItemHover={(itemId: string | null) => {
        const hoveredItem = items.find((item) => item.id === itemId);
        onHoverEvent(hoveredItem?.opensDrawer ? hoveredItem.event.eventId : null);
      }}
      onItemSelect={(itemId: string) => {
        const selectedItem = items.find((item) => item.id === itemId);
        if (!selectedItem) return;
        if (selectedItem.opensDrawer) {
          onSelectEvent(selectedItem.event);
          return;
        }
        onSelectBoundary(selectedItem.event);
      }}
      renderMarker={(timelineItem: ActivityTimelineItem) => {
        const item = items.find((entry) => entry.id === timelineItem.id);
        if (!item) return null;

        const markerContext = { event: item.event, item: { color: item.color, type: item.type } };

        if (markerTemplateLoaded && markerTemplate) {
          return (
            <VisibleTimelineMarker itemId={item.id} onVisibleChange={handleVisibleChange}>
              {evaluate(markerTemplate, markerContext) as JSX.Element}
            </VisibleTimelineMarker>
          );
        }

        if (item.type === 'event') {
          return (
            <VisibleTimelineMarker itemId={item.id} onVisibleChange={handleVisibleChange}>
              <Box
                style={{
                  backgroundColor: item.color,
                  borderRadius: '50%',
                  height: 16,
                  width: 16,
                }}
              />
            </VisibleTimelineMarker>
          );
        }

        return (
          <VisibleTimelineMarker itemId={item.id} onVisibleChange={handleVisibleChange}>
            <Stack
              alignItems="center"
              justifyContent="center"
              style={{
                backgroundColor: item.color,
                borderRadius: '50%',
                color: '#fff',
                height: 24,
                width: 24,
              }}
            >
              <Icon fontSize="extra-small" icon={item.type === 'start' ? 'ArrowDown' : 'Flag'} />
            </Stack>
          </VisibleTimelineMarker>
        );
      }}
    />
  );
};
